import axios from './axios';

/**
 * Get all projects stored in EntityData (Type=Project)
 */
export const loadProjects = async () => {
  const response = await axios.get('projects/');
  return response.data;
};

/**
 * Create a new project by name (without initial TTL upload)
 */
export const createProject = async (name) => {
  const response = await axios.post('projects/create/', { name });
  return response.data;
};

/**
 * Upload a new project TTL file and create EntityData record
 */
export const uploadProjectTTL = async (formData, onUploadProgress) => {
  const response = await axios.post('projects/upload/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000,
    onUploadProgress,
  });
  return response.data;
};

/**
 * Delete an existing project
 */
export const deleteProject = async (projectId) => {
  const response = await axios.delete(`projects/${projectId}/delete/`);
  return response.data;
};

/**
 * Rename an existing project
 */
export const renameProject = async (projectId, name) => {
  const response = await axios.patch(`projects/${projectId}/rename/`, { name });
  return response.data;
};

/**
 * Get required ontologies and load status for one project
 */
export const loadProjectOntologies = async (projectId, viewKey = 'vs1') => {
  const response = await axios.get(`projects/${projectId}/ontologies/`, {
    params: { view_key: viewKey },
  });
  return response.data;
};

/**
 * Upload one ontology file for a given project ontology slot
 */
export const uploadProjectOntology = async (projectId, ontologyKey, file, viewKey = 'vs1', onUploadProgress) => {
  const formData = new FormData();
  formData.append('ontology_key', ontologyKey);
  formData.append('view_key', viewKey);
  formData.append('file', file);

  const response = await axios.post(`projects/${projectId}/ontologies/upload/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000,
    onUploadProgress,
  });

  return response.data;
};

/**
 * Apply ontologies by linking available ontology entities to the selected project view
 */
export const applyProjectOntologies = async (projectId, viewKey = 'vs1') => {
  const response = await axios.post(`projects/${projectId}/ontologies/apply/`, {
    view_key: viewKey,
  });
  return response.data;
};

/**
 * Get VS1/VS2/Documents state for a project
 */
export const loadProjectViews = async (projectId) => {
  const response = await axios.get(`projects/${projectId}/views/`);
  return response.data;
};

/**
 * Get VS sections (Objects, Classes, Requirements, Verification Plans)
 * for one project view and source (project or ontology)
 */
export const loadProjectViewSections = async (projectId, viewKey = 'vs1', source = 'project') => {
  const response = await axios.get(`projects/${projectId}/views/sections/`, {
    params: {
      view_key: viewKey,
      source,
    },
    // Large projects can take >60s to build VS sections server-side.
    // Avoid client-side timeout while backend is still processing.
    timeout: 0,
  });
  return response.data;
};

/**
 * Update applicability status for one project-linked EntityData row.
 */
export const updateProjectEntityApplicability = async (projectId, entityId, applicabilityStatus) => {
  const response = await axios.patch(`projects/${projectId}/entities/${entityId}/applicability/`, {
    applicability_status: applicabilityStatus,
  });
  return response.data;
};

/**
 * Run backend object applicability analysis and persist aplica/no_aplica + reason.
 */
export const analyzeProjectObjectApplicability = async (projectId, viewKey = 'vs1', entityId = null) => {
  const response = await axios.post(`projects/${projectId}/objects/applicability/analyze/`, {
    view_key: viewKey,
    ...(entityId ? { entity_id: entityId } : {}),
  }, {
    // This endpoint can run for several minutes when many objects are evaluated with LLM checks.
    // Disable client timeout so frontend does not fail while backend is still processing.
    timeout: 0,
  });
  return response.data;
};

/**
 * Run the backend requirement applicability first pass and persist the LLM classification.
 */
export const analyzeProjectRequirementApplicability = async (projectId, viewKey = 'vs1', source = 'project', entityId = null) => {
  const response = await axios.post(`projects/${projectId}/requirements/applicability/analyze/`, {
    view_key: viewKey,
    source,
    ...(entityId ? { entity_id: entityId } : {}),
  }, {
    timeout: 0,
  });
  return response.data;
};

/**
 * Analyze a single requirement entity for applicability (first + second pass with full document context).
 * Calls the same endpoint as batch analysis but with entity_id parameter to analyze just one entity.
 */
export const analyzeProjectRequirementApplicabilitySingle = async (projectId, entityId, viewKey = 'vs1', source = 'project') => {
  return analyzeProjectRequirementApplicability(projectId, viewKey, source, entityId);
};

/**
 * Get all mention locations for one object applicability candidate node.
 */
export const loadProjectObjectCandidateLocations = async (projectId, entityId, nodeId, viewKey = 'vs1') => {
  const response = await axios.get(`projects/${projectId}/objects/${entityId}/applicability/candidate-locations/`, {
    params: {
      node_id: nodeId,
      view_key: viewKey,
    },
  });
  return response.data;
};

/**
 * Get original + translated text for a project document page.
 */
export const loadProjectDocumentPageContent = async (projectId, documentId, pageNumber = null) => {
  const params = {};
  if (pageNumber !== null && pageNumber !== undefined && pageNumber !== '') {
    params.page_number = pageNumber;
  }

  const response = await axios.get(`projects/${projectId}/documents/${documentId}/page-content/`, {
    params,
  });
  return response.data;
};

/**
 * Get entity graph data (nodes + edges) for a project
 */
export const loadProjectEntityGraph = async (projectId) => {
  const response = await axios.get(`projects/${projectId}/entity-graph/`);
  return response.data;
};

/**
 * Get project-level ingestion knowledge graph (all project documents, optional filters).
 */
export const loadProjectDocumentsGraph = async (projectId, params = {}) => {
  const requestParams = {
    include_mentions: false,
    include_mention_text: false,
    include_extended_fields: false,
    ...params,
  };
  const response = await axios.get(`projects/${projectId}/documents/graph/`, { params: requestParams });
  return response.data;
};

/**
 * Queue project-level ingestion knowledge graph regeneration.
 */
export const generateProjectDocumentsGraph = async (projectId, payload = {}) => {
  const response = await axios.post(`projects/${projectId}/documents/graph/generate/`, payload);
  return response.data;
};

/**
 * Apply VS1 by rebuilding project entities/relationships from the VS1 TTL.
 */
export const applyProjectVs1 = async (projectId) => {
  const response = await axios.post(`projects/${projectId}/views/vs1/apply/`);
  return response.data;
};

/**
 * Upload one file to a specific project view (vs1, vs2, documents)
 */
export const uploadProjectViewFile = async (projectId, viewKey, file, extraFields = {}, onUploadProgress) => {
  let resolvedExtraFields = extraFields;
  let resolvedOnUploadProgress = onUploadProgress;

  if (typeof extraFields === 'function') {
    resolvedOnUploadProgress = extraFields;
    resolvedExtraFields = {};
  }

  const formData = new FormData();
  formData.append('view_key', viewKey);
  formData.append('file', file);

  if (resolvedExtraFields && typeof resolvedExtraFields === 'object') {
    Object.entries(resolvedExtraFields).forEach(([fieldKey, fieldValue]) => {
      if (fieldValue === undefined || fieldValue === null) {
        return;
      }

      const normalizedValue = String(fieldValue).trim();
      if (!normalizedValue) {
        return;
      }

      formData.append(fieldKey, normalizedValue);
    });
  }

  if (viewKey === 'documents') {
    const response = await axios.post(`projects/${projectId}/views/upload/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
      onUploadProgress: resolvedOnUploadProgress,
    });

    return {
      ...response.data,
      __upload_route: 'project_view_documents',
    };
  }

  const response = await axios.post(`projects/${projectId}/views/upload/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000,
    onUploadProgress: resolvedOnUploadProgress,
  });

  return response.data;
};

/**
 * Poll progress of server-side document processing for a project upload task.
 */
export const getProjectViewUploadProgress = async (projectId, taskId) => {
  const response = await axios.get(`projects/${projectId}/views/upload-progress/${encodeURIComponent(taskId)}/`);
  return response.data;
};

/**
 * Poll progress of server-side document processing for global ingestion uploads.
 */
export const getIngestionUploadProgress = async (taskId) => {
  const response = await axios.get(`ingestion/upload-progress/${encodeURIComponent(taskId)}/`);
  return response.data;
};

/**
 * Cancel an ongoing ingestion upload task
 */
export const cancelIngestionUpload = async (taskId) => {
  const response = await axios.post(`ingestion/upload-cancel/${encodeURIComponent(taskId)}/`);
  return response.data;
};

/**
 * Download a project view TTL file (original or English translation)
 */
export const downloadProjectViewTTL = async (projectId, viewKey, lang = null) => {
  const params = { view_key: viewKey };
  if (lang) params.lang = lang;
  const response = await axios.get(`projects/${projectId}/views/download/`, {
    params,
    responseType: 'blob',
  });
  return response;
};

/**
 * Download an ontology TTL file (original or English translation)
 */
export const downloadProjectOntologyTTL = async (projectId, ontologyKey, viewKey = 'vs1', lang = null) => {
  const params = { view_key: viewKey, ontology_key: ontologyKey };
  if (lang) params.lang = lang;
  const response = await axios.get(`projects/${projectId}/ontologies/download/`, {
    params,
    responseType: 'blob',
  });
  return response;
};

/**
 * Unlink an uploaded document from a project without deleting the underlying file.
 */
export const deleteProjectDocument = async (projectId, repositoryDocumentEntityId) => {
  const response = await axios.delete(`projects/${projectId}/documents/${repositoryDocumentEntityId}/unlink/`);
  return response.data;
};

export const projectApi = {
  loadProjects,
  createProject,
  uploadProjectTTL,
  deleteProject,
  renameProject,
  loadProjectOntologies,
  uploadProjectOntology,
  applyProjectOntologies,
  loadProjectViews,
  loadProjectViewSections,
  updateProjectEntityApplicability,
  analyzeProjectObjectApplicability,
  analyzeProjectRequirementApplicability,
  applyProjectVs1,
  loadProjectDocumentsGraph,
  generateProjectDocumentsGraph,
  uploadProjectViewFile,
  getProjectViewUploadProgress,
  getIngestionUploadProgress,
  cancelIngestionUpload,
  downloadProjectViewTTL,
  downloadProjectOntologyTTL,
  deleteProjectDocument,
};

export default projectApi;
